import React, { useMemo, useState } from 'react';
import { Container, Grid, GridItem, Heading, Text } from '@charlietango/ui';
import { rem } from '../../styles/rem';

import { defaultPartyDetailsUrl } from '../../application/PartyDetailsUrlContext';
import { DeclarationOverviewViewModel } from '../../components/DeclarationOverview/types/DeclarationOverviewViewModel';
import PartyList from '../../components/PartyList/PartyList';
import {
  getPartySortOptionLabel,
  mergePartyElectionTypes,
  partySortOptions,
  PartySortBy,
  sortParties,
} from '../../components/PartyList/partyListModel';
import type {
  PartyListSortBy,
  PartyTableSortOption,
  SortDirection,
} from '../../components/PartyList/partyListModel';
import { useParties } from '../../api/hooks/useParties';
import Placeholder from '../../components/Placeholder/Placeholder';
import { useDictionary } from '../../hooks/useDictionary';
import { useElectionType } from '../../hooks/useSearchParams';
import { FontFamily, FontWeight, TextVariant } from '../../styles/typography';
import AccordionViewModel from '../../view-models/AccordionViewModel';
import TextBlockViewModel from '../../view-models/TextBlockViewModel';
import PartiesFiltering from './PartiesFiltering';
import { colors } from '../../styles/colors';
import Button from '../../components/Button/Button';
import { ButtonVariant } from '../../styles/buttons';
import ArrowDownIcon from '../../icons/ArrowDownIcon';
import RichText from '../../components/RichText/RichText';
import MobilePartySortControls from './MobilePartySortControls';

export type ThePartiesPageProps = {
  partyDetailsUrl?: string;
  declarationOverview?: DeclarationOverviewViewModel;
  info?: TextBlockViewModel;
  faq?: AccordionViewModel;
};

const columnHeaderStyle = {
  fontSize: 2,
  fontWeight: FontWeight.Medium,
  lineHeight: 1.75,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: rem(8),
  width: 'auto',
  maxWidth: '100%',
  ml: 0,
  px: 0,
  textDecoration: 'none',
  '& span': {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  'span:first-of-type': {
    flex: '0 1 auto',
  },
  'span[aria-hidden="true"]': {
    position: 'static',
    flex: '0 0 auto',
    top: 'auto',
    right: 'auto',
    transform: 'none',
    color: colors.base.Icon.BrandStatic,
  },
  svg: {
    color: colors.base.Icon.BrandStatic,
    width: rem(12),
    height: rem(12),
  },
  color: colors.base.Text.Primary,
  '&:hover, &:focus-visible': {
    color: colors.base.Text.Primary,
    svg: {
      color: colors.base.Icon.BrandStatic,
      width: rem(12),
      height: rem(12),
    },
  },
};

function ThePartiesPage({ partyDetailsUrl }: ThePartiesPageProps) {
  const { t } = useDictionary();
  const { electionType } = useElectionType();
  const [filter, setFilter] = useState('');
  const [sorting, setSorting] = useState<PartyListSortBy>(PartySortBy.Name);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const resolvedPartyDetailsUrl = partyDetailsUrl || defaultPartyDetailsUrl;

  const mobileSortOptions = partySortOptions.map((option) => ({
    value: option.value,
    label: getPartySortOptionLabel(option, t),
  }));

  const handleSort = (sortBy: PartyListSortBy) => {
    if (sorting === sortBy) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSorting(sortBy);
      setSortDirection('asc');
    }
  };

  const handleMobileSortSelect = (nextSorting: PartyListSortBy) => {
    setSorting(nextSorting);
    setSortDirection('asc');
  };

  const renderColumnHeader = (option: PartyTableSortOption) => (
    <Button
      variant={ButtonVariant.Link}
      onClick={() => handleSort(option.value)}
      sx={{
        ...columnHeaderStyle,
        ...(option.value === PartySortBy.AlsoEuroparliament
          ? {
              justifyContent: 'flex-end',
              minWidth: 'fit-content',
              '& span': {
                textOverflow: 'clip',
                overflow: 'visible',
                whiteSpace: 'nowrap',
              },
            }
          : {}),
      }}
      icon={
        <ArrowDownIcon
          style={
            sorting === option.value && sortDirection === 'asc'
              ? { transform: 'rotate(180deg)' }
              : {}
          }
        />
      }
    >
      {getPartySortOptionLabel(option, t)}
    </Button>
  );

  const { data: parties, isSuccess } = useParties(electionType);
  const mergedItems = useMemo(
    () => mergePartyElectionTypes(parties?.items || []),
    [parties?.items],
  );

  let items = mergedItems;

  if (items) {
    if (filter) {
      items = items.filter((item) =>
        item.name?.toLowerCase()?.includes(filter.toLowerCase()),
      );
    }

    items = sortParties(items, sorting, sortDirection);
  }

  return (
    <>
      <Container
        sx={{
          maxWidth: 'unset',
          mx: 0,
          px: [rem(20), rem(20), 0],
          py: rem(80),
          borderBottom: '1px solid rgba(31, 111, 111, 0.20)',
        }}
      >
        <div
          sx={{
            maxWidth: rem(952),
            mx: 'auto',
          }}
        >
          <Heading
            as="h1"
            id="parties-header"
            variant={TextVariant.Header3xl}
            sx={{
              mb: [rem(16), rem(16), rem(20)],
              fontSize: [rem(32), rem(32), rem(48)],
              lineHeight: [rem(40), rem(40), rem(56)],
            }}
          >
            Partierne
          </Heading>
          <Text variant={TextVariant.BodyLg} as="p" sx={{ mb: 0 }}>
            Liste med alle partier der stiller op.
          </Text>
          <Grid>
            <GridItem size={[1, null, 7 / 24]}>
              <div sx={{ mx: [0], mt: [rem(44), rem(44), rem(40)] }}>
                <PartiesFiltering
                  filter={filter}
                  onFilter={setFilter}
                  label="Søg efter parti"
                />
              </div>
            </GridItem>
          </Grid>
        </div>
      </Container>
      <Container sx={{ mx: 0, maxWidth: 'unset', px: [rem(20), rem(20), 0] }}>
        <Container
          sx={{
            pb: ['4rem', null, '5rem'],
            pt: ['3rem', null, rem(40)],
            px: [0, null, 0],
          }}
        >
          {isSuccess ? (
            <h2
              sx={{
                mt: 0,
                mb: rem(24),
                mx: [0, null, rem(64)],
                display: ['none', 'none', 'block'],
                fontFamily: FontFamily.Heading,
                fontSize: rem(16),
                fontWeight: FontWeight.Medium,
                lineHeight: rem(24),
                letterSpacing: 0,
                color: colors.base.Text.Primary,
                span: {
                  fontFamily: FontFamily.Body,
                  fontWeight: FontWeight.Normal,
                  color: colors.base.Text.Secondary,
                },
              }}
            >
              Partier <span>({items ? items.length : 0})</span>
            </h2>
          ) : null}
          <Grid>
            <GridItem size={[1]}>
              {isSuccess ? (
                <MobilePartySortControls
                  count={items ? items.length : 0}
                  options={mobileSortOptions}
                  sorting={sorting}
                  sortDirection={sortDirection}
                  onSelectSort={handleMobileSortSelect}
                  onToggleDirection={() =>
                    setSortDirection((prev) =>
                      prev === 'asc' ? 'desc' : 'asc',
                    )
                  }
                />
              ) : null}
              {isSuccess ? (
                <PartyList
                  partyDetailsUrl={resolvedPartyDetailsUrl}
                  items={items}
                  highlight={filter}
                  renderTableHeader={renderColumnHeader}
                  aria-labelledby="parties-header"
                  aria-live="polite"
                  id="parties-list"
                />
              ) : (
                <div
                  role="status"
                  aria-label="Indlæser partier"
                  sx={{ minHeight: '100vh' }}
                >
                  <Placeholder css={{ height: '100vh' }} />
                </div>
              )}
            </GridItem>
          </Grid>
          {isSuccess ? (
            <RichText
              variant={TextVariant.BodyMd}
              sx={{
                mb: [8],
                mt: [rem(56), rem(56), rem(80)],
                mx: [0, 0, 8],
                textAlign: ['left', 'left', 'right'],
                '> p': {
                  my: 0,
                  lineHeight: rem(20),
                },
                '> p + p': {
                  mt: rem(2),
                },
              }}
            >
              {t('parties.partiesDisclaimer')}
            </RichText>
          ) : null}
        </Container>
      </Container>
    </>
  );
}

ThePartiesPage.displayName = 'ThePartiesPage';

export default ThePartiesPage;
